import * as React from 'react';
import {
    NavigateNext,
    AppsOutlined,
    PublicOutlined,
    DescriptionOutlined,
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';

const Root = styled('div')({
    display: 'inline-flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 2,
    padding: '6px 10px',
    borderRadius: 999,
});

const PaperRoot = styled(Root)({
    background: 'rgba(255, 255, 255, 0.78)',
    backdropFilter: 'saturate(180%) blur(14px)',
    WebkitBackdropFilter: 'saturate(180%) blur(14px)',
    border: '1px solid rgba(11, 18, 48, 0.08)',
    boxShadow: '0 1px 2px rgba(11, 18, 48, 0.03), 0 8px 24px -16px rgba(11, 18, 48, 0.18)',
});

const linkSx = {
    fontSize: 13.5,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 0.6,
    px: 1,
    py: 0.5,
    borderRadius: 999,
    textDecoration: 'none',
    color: 'text.secondary',
    fontWeight: 500,
    letterSpacing: '-0.003em',
    transition: 'background-color 160ms ease, color 160ms ease',
    '&:hover': {
        backgroundColor: 'rgba(5, 35, 114, 0.06)',
        color: 'primary.main',
        textDecoration: 'none',
    },
} as const;

const leafSx = {
    fontSize: 13.5,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 0.6,
    px: 1,
    py: 0.5,
    borderRadius: 999,
    fontWeight: 600,
    letterSpacing: '-0.003em',
    color: 'text.primary',
    background: 'linear-gradient(180deg, rgba(5, 35, 114, 0.06) 0%, rgba(5, 35, 114, 0.02) 100%)',
} as const;

const arrowSx = {
    color: 'text.disabled',
    fontSize: 14,
    mx: 0,
} as const;

type BreadcumbItem = {
    to?: string;
    label: string;
    icon?: React.ReactNode;
};

type BreadcumbProps = {
    paperStyle?: boolean;
    items: Array<BreadcumbItem | null | undefined>;
};

// URL conventions in this project:
//   /websites/<theme>/                  -> list of all sites in a theme
//   /websites/<theme>/<websiteKey>/     -> a single website's home
//   /websites/<theme>/<websiteKey>/...  -> a deeper editor surface
function inferIcon(item: BreadcumbItem): React.ReactNode | null {
    if (item.icon !== undefined) return item.icon;
    if (!item.to) return null;
    const segments = item.to.split('/').filter(Boolean);
    if (segments[0] !== 'websites') return null;
    if (segments.length === 2) return <AppsOutlined sx={{ fontSize: 15 }} />;
    if (segments.length === 3) return <PublicOutlined sx={{ fontSize: 15 }} />;
    return <DescriptionOutlined sx={{ fontSize: 15 }} />;
}

export class Breadcumb extends React.Component<BreadcumbProps, {}> {

    render() {
        const validItems = (this.props.items || []).filter(Boolean) as BreadcumbItem[];
        const lastIndex = validItems.length - 1;

        const Wrapper = this.props.paperStyle !== false ? PaperRoot : Root;

        return (
            <Wrapper>
                {validItems.map((item, i) => {
                    const isLeaf = i === lastIndex;
                    const icon = inferIcon(item);
                    const labelNode = (
                        <>
                            {icon}
                            <Box component="span">{item.label}</Box>
                        </>
                    );
                    return (
                        <React.Fragment key={`item-${i}-${item.label}`}>
                            {i > 0 && (
                                <NavigateNext sx={arrowSx} aria-hidden />
                            )}
                            {item.to && !isLeaf ? (
                                <Typography
                                    component={Link as any}
                                    to={item.to}
                                    sx={linkSx}
                                    variant="body2"
                                >
                                    {labelNode}
                                </Typography>
                            ) : (
                                <Typography sx={leafSx} variant="body2">
                                    {labelNode}
                                </Typography>
                            )}
                        </React.Fragment>
                    );
                })}
            </Wrapper>
        );
    }
}
